import Highcharts, { Series } from 'highcharts';
import { removeVerticalLabel, renderVerticalLabel } from './renderVerticalLabel';
import { removeAnnotation, renderAnnotation } from './renderAnnotation';
import { formatCurrency } from '../../../../../../../CommonUtils/formatNumber';
import { Theme } from '../../../../../../../theme/theme';
import {
  AnnotationsIds,
  PlanSeriesName,
  RetirementChartMilestoneAnnotations,
} from '../RetirementChart.meta';

export type ChartType = 'overview' | 'montecarlo';

export const chartRenderEvent = (
  chart: Highcharts.Chart,
  data: RetirementChartMilestoneAnnotations,
  theme: Theme,
  type: ChartType,
) => {
  const colorSuccess = theme.colors.success['600'];
  const colorError = theme.colors.error['600'];
  let proposedPlanSeries: Series | null = null;

  if (type === 'overview') {
    proposedPlanSeries =
      chart.series.find(({ name }) => name === PlanSeriesName.Recommended) ?? null;
  } else {
    proposedPlanSeries = chart.series[0];
  }

  if (!proposedPlanSeries || proposedPlanSeries.points.length === 0) {
    return;
  }

  const {
    PrincipalName,
    PrincipalRetirementIndex,
    PrincipalRetirementAge,
    PrincipalLifeExpectancyIndex,
    PrincipalLifeExpectancy,
    HasSpouse,
    SpouseName,
    SpouseRetirementIndex,
    SpouseRetirementAge,
    SpouseLifeExpectancyIndex,
    SpouseLifeExpectancy,
  } = data;

  const principalRetirementPoint =
    PrincipalRetirementIndex !== null ? proposedPlanSeries.points[PrincipalRetirementIndex] : null;
  const spouseRetirementPoint =
    SpouseRetirementIndex !== null ? proposedPlanSeries.points[SpouseRetirementIndex] : null;

  if (principalRetirementPoint) {
    renderVerticalLabel({
      id: AnnotationsIds.PrincipalRetirement,
      chart,
      point: principalRetirementPoint,
      value: PrincipalRetirementAge,
      label: HasSpouse ? `${PrincipalName}'s <br/> Retirement Age` : 'Retirement Age',
      offset: 0,
      color: theme.colors.primary['600'],
      theme,
    });
  } else {
    removeVerticalLabel(AnnotationsIds.PrincipalRetirement);
  }

  const principalLifeExpectancyPoint =
    PrincipalLifeExpectancyIndex !== null
      ? proposedPlanSeries.points[PrincipalLifeExpectancyIndex]
      : null;

  if (principalLifeExpectancyPoint) {
    renderVerticalLabel({
      id: AnnotationsIds.PrincipalLife,
      chart,
      point: principalLifeExpectancyPoint,
      value: PrincipalLifeExpectancy,
      label: HasSpouse ? `${PrincipalName}'s <br/> Life Expectancy` : 'Life Expectancy',
      offset: 0,
      color:
        principalLifeExpectancyPoint.y && principalLifeExpectancyPoint.y > 0
          ? colorSuccess
          : colorError,
      theme,
    });
  } else {
    removeVerticalLabel(AnnotationsIds.PrincipalLife);
  }

  if (HasSpouse) {
    if (spouseRetirementPoint) {
      renderVerticalLabel({
        id: AnnotationsIds.SpouseRetirement,
        chart,
        point: spouseRetirementPoint,
        value: SpouseRetirementAge,
        label: `${SpouseName}'s <br/> Retirement Age`,
        offset: 45,
        color: theme.colors.primary['600'],
        theme,
      });
    } else {
      removeVerticalLabel(AnnotationsIds.SpouseRetirement);
    }

    const spouseLifeExpectancyPoint =
      SpouseLifeExpectancyIndex !== null
        ? proposedPlanSeries.points[SpouseLifeExpectancyIndex]
        : null;

    if (spouseLifeExpectancyPoint) {
      renderVerticalLabel({
        id: AnnotationsIds.SpouseLife,
        chart,
        point: spouseLifeExpectancyPoint,
        value: SpouseLifeExpectancy,
        label: `${SpouseName}'s <br/> Life Expectancy`,
        offset: 45,
        color:
          spouseLifeExpectancyPoint.y && spouseLifeExpectancyPoint.y > 0
            ? colorSuccess
            : colorError,
        theme,
      });
    } else {
      removeVerticalLabel(AnnotationsIds.SpouseLife);
    }
  } else {
    removeVerticalLabel(AnnotationsIds.SpouseRetirement);
    removeVerticalLabel(AnnotationsIds.SpouseLife);
  }

  const isSpouseRetiringFirst =
    HasSpouse &&
    PrincipalRetirementIndex !== null &&
    SpouseRetirementIndex !== null &&
    PrincipalRetirementIndex > SpouseRetirementIndex;

  const retirementPoint = isSpouseRetiringFirst ? spouseRetirementPoint : principalRetirementPoint;

  if (retirementPoint && type === 'overview') {
    renderAnnotation({
      id: AnnotationsIds.FundsAtRetirement,
      chart,
      point: retirementPoint,
      className: 'funds-annotation',
      // ADV-5867 - Hide the label when the amount is 0
      label: retirementPoint?.y
        ? `<span style='text-align: center; font-size: 12px; font-weight: 500; fill: ${
            theme.colors.primary['500']
          };'>Funds at Retirement Start</span><br><span style='font-size: 12px; font-weight: 600; text-align: center; fill: ${
            theme.colors.primary['500']
          };'>${formatCurrency(retirementPoint.y)}</span>`
        : '',
    });
  } else {
    removeAnnotation(AnnotationsIds.FundsAtRetirement);
  }
};
